import React from 'react';
import { Document, Page, View, Text, StyleSheet, Image } from '@react-pdf/renderer';
import { Vehicle } from '../../types';

const toNum = (v: unknown): number => {
  if (typeof v === 'number' && !Number.isNaN(v)) return v;
  const s = String(v ?? '').trim();
  if (s === '') return 0;
  if (/,\d*$/.test(s)) return Number(s.replace(/\./g, '').replace(',', '.')) || 0;
  return parseFloat(s) || 0;
};

const formatCurrency = (value: unknown): string =>
  toNum(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatDateBR = (dateString?: string): string => {
  if (!dateString) return '—';
  const d = String(dateString).split('T')[0];
  const [y, m, day] = d.split('-');
  if (!y || !m || !day) return d;
  return `${day}/${m}/${y}`;
};

export interface ProposalFormData {
  customerName: string;
  customerPhone?: string;
  customerDocument?: string;
  validUntil?: string; // YYYY-MM-DD
  vehiclePrice: number;
  discount: number;
  downPayment: number;
  tradeInValue: number;
  fees: number;
  installmentsCount: number;
  installmentsValue: number;
  notes?: string;
}

export interface ProposalPDFProps {
  vehicle: Vehicle;
  vehicleImageUrl?: string | null;
  logoUrl?: string | null;
  generatedAtLabel: string;
  proposalNumber?: string;
  data: ProposalFormData;
}

const styles = StyleSheet.create({
  page: { padding: 32, fontSize: 10, fontFamily: 'Helvetica', color: '#111827' },
  headerRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 14, gap: 12, alignItems: 'center' },
  logo: { width: 150, height: 38, objectFit: 'contain' },
  title: { fontSize: 18, fontWeight: 700 },
  subtitle: { marginTop: 4, color: '#6B7280' },
  badge: {
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 999,
    paddingVertical: 4,
    paddingHorizontal: 10,
    alignSelf: 'flex-start',
    color: '#111827',
    backgroundColor: '#F9FAFB',
    fontSize: 9,
  },
  sectionTitle: { fontSize: 12, fontWeight: 700, marginTop: 10, marginBottom: 8 },
  box: { borderWidth: 1, borderColor: '#E5E7EB', borderRadius: 10, padding: 12, marginBottom: 12 },
  row: { flexDirection: 'row', justifyContent: 'space-between', gap: 10 },
  col: { flexGrow: 1, minWidth: 0 },
  label: { color: '#6B7280' },
  value: { marginTop: 2, fontWeight: 700 },
  vehicleTop: { flexDirection: 'row', gap: 12 },
  vehicleImg: { width: 168, height: 108, objectFit: 'cover', borderRadius: 8 },
  vehicleImgPlaceholder: { width: 168, height: 108, borderRadius: 8, backgroundColor: '#F3F4F6' },
  vehicleName: { fontSize: 12, fontWeight: 700 },
  vehicleMeta: { marginTop: 4, color: '#374151' },
  table: { borderWidth: 1, borderColor: '#E5E7EB', borderRadius: 10, overflow: 'hidden' },
  tableHeader: { flexDirection: 'row', backgroundColor: '#F9FAFB', borderBottomWidth: 1, borderBottomColor: '#E5E7EB', paddingVertical: 8, paddingHorizontal: 10 },
  tableRow: { flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: '#F3F4F6', paddingVertical: 8, paddingHorizontal: 10 },
  cellDesc: { flexGrow: 1 },
  cellVal: { width: 110, textAlign: 'right', fontWeight: 700 },
  totalRow: { backgroundColor: '#111827' },
  totalLabel: { color: '#fff', fontWeight: 700 },
  totalVal: { color: '#fff', width: 110, textAlign: 'right', fontWeight: 700 },
  footnote: { marginTop: 10, color: '#6B7280', fontSize: 9, lineHeight: 1.25 },
  footer: { position: 'absolute', bottom: 22, left: 32, right: 32, fontSize: 8, color: '#9CA3AF', textAlign: 'center' },
});

const getVehicleIdLabel = (v: Vehicle): string => {
  if ((v as any).identification) return String((v as any).identification);
  if ((v as any).no_plate) return 'Sem placa';
  return String((v as any).plate || '—');
};

const ProposalPDF: React.FC<ProposalPDFProps> = ({
  vehicle,
  vehicleImageUrl,
  logoUrl,
  generatedAtLabel,
  proposalNumber,
  data,
}) => {
  const vehiclePrice = toNum(data.vehiclePrice);
  const discount = Math.max(0, toNum(data.discount));
  const tradeInValue = Math.max(0, toNum(data.tradeInValue));
  const fees = Math.max(0, toNum(data.fees));

  const total = Math.max(0, vehiclePrice - discount - tradeInValue + fees);
  const downPayment = Math.min(Math.max(0, toNum(data.downPayment)), total);
  const remaining = Math.max(0, total - downPayment);

  const installmentsCount = Math.max(0, Math.floor(toNum(data.installmentsCount)));
  const installmentsValue = toNum(data.installmentsValue);

  const hasInstallments = installmentsCount > 0 && (installmentsValue > 0 || remaining > 0);
  const autoInstallmentValue = installmentsCount > 0 ? remaining / installmentsCount : 0;
  const finalInstallmentValue = installmentsValue > 0 ? installmentsValue : autoInstallmentValue;

  return (
    <Document title={`Proposta - ${vehicle.brand} ${vehicle.model}`}>
      <Page size="A4" style={styles.page}>
        <View style={styles.headerRow}>
          {logoUrl ? <Image src={logoUrl} style={styles.logo} /> : <View />}
          <View style={{ flexGrow: 1, minWidth: 0 }}>
            <Text style={styles.title}>Proposta Comercial</Text>
            <Text style={styles.subtitle}>Gerado em: {generatedAtLabel}</Text>
            {data.validUntil ? <Text style={styles.subtitle}>Válida até: {formatDateBR(data.validUntil)}</Text> : null}
          </View>
          <View>
            <Text style={styles.badge}>{proposalNumber ? `Proposta #${proposalNumber}` : 'Proposta'}</Text>
          </View>
        </View>

        <Text style={styles.sectionTitle}>Cliente</Text>
        <View style={styles.box}>
          <View style={styles.row}>
            <View style={styles.col}>
              <Text style={styles.label}>Nome</Text>
              <Text style={styles.value}>{data.customerName || '—'}</Text>
            </View>
            <View style={styles.col}>
              <Text style={styles.label}>Telefone</Text>
              <Text style={styles.value}>{data.customerPhone || '—'}</Text>
            </View>
            <View style={styles.col}>
              <Text style={styles.label}>CPF/CNPJ</Text>
              <Text style={styles.value}>{data.customerDocument || '—'}</Text>
            </View>
          </View>
        </View>

        <Text style={styles.sectionTitle}>Veículo</Text>
        <View style={styles.box}>
          <View style={styles.vehicleTop}>
            {vehicleImageUrl ? <Image src={vehicleImageUrl} style={styles.vehicleImg} /> : <View style={styles.vehicleImgPlaceholder} />}
            <View style={styles.col}>
              <Text style={styles.vehicleName}>
                {vehicle.brand} {vehicle.model} ({vehicle.year})
              </Text>
              <Text style={styles.vehicleMeta}>Identificação: {getVehicleIdLabel(vehicle)}</Text>
              <Text style={styles.vehicleMeta}>Cor: {vehicle.color || '—'} • KM: {(vehicle as any).km || '—'}</Text>
              <Text style={styles.vehicleMeta}>Câmbio: {vehicle.cambio || '—'} • Motor: {vehicle.motor || '—'}</Text>
              {(vehicle as any).is_consignado ? <Text style={styles.vehicleMeta}>Consignado: Sim</Text> : null}
            </View>
          </View>
        </View>

        <Text style={styles.sectionTitle}>Condições</Text>
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={styles.cellDesc}>Item</Text>
            <Text style={styles.cellVal}>Valor</Text>
          </View>
          <View style={styles.tableRow}>
            <Text style={styles.cellDesc}>Valor do veículo</Text>
            <Text style={styles.cellVal}>{formatCurrency(vehiclePrice)}</Text>
          </View>
          {discount > 0 ? (
            <View style={styles.tableRow}>
              <Text style={styles.cellDesc}>Desconto</Text>
              <Text style={styles.cellVal}>− {formatCurrency(discount)}</Text>
            </View>
          ) : null}
          {tradeInValue > 0 ? (
            <View style={styles.tableRow}>
              <Text style={styles.cellDesc}>Avaliação / troca (abatimento)</Text>
              <Text style={styles.cellVal}>− {formatCurrency(tradeInValue)}</Text>
            </View>
          ) : null}
          {fees > 0 ? (
            <View style={styles.tableRow}>
              <Text style={styles.cellDesc}>Taxas / serviços</Text>
              <Text style={styles.cellVal}>{formatCurrency(fees)}</Text>
            </View>
          ) : null}
          <View style={[styles.tableRow, styles.totalRow]}>
            <Text style={[styles.cellDesc, styles.totalLabel]}>Total</Text>
            <Text style={styles.totalVal}>{formatCurrency(total)}</Text>
          </View>
          <View style={styles.tableRow}>
            <Text style={styles.cellDesc}>Entrada</Text>
            <Text style={styles.cellVal}>{formatCurrency(downPayment)}</Text>
          </View>
          <View style={styles.tableRow}>
            <Text style={styles.cellDesc}>Saldo a pagar</Text>
            <Text style={styles.cellVal}>{formatCurrency(remaining)}</Text>
          </View>
          {hasInstallments ? (
            <View style={styles.tableRow}>
              <Text style={styles.cellDesc}>Parcelas</Text>
              <Text style={styles.cellVal}>
                {installmentsCount}× {formatCurrency(finalInstallmentValue)}
              </Text>
            </View>
          ) : null}
        </View>

        {data.notes ? (
          <>
            <Text style={styles.sectionTitle}>Observações</Text>
            <View style={styles.box}>
              <Text style={{ fontSize: 10, lineHeight: 1.35 }}>{String(data.notes)}</Text>
            </View>
          </>
        ) : null}

        <Text style={styles.footnote}>
          Valores e condições sujeitos à disponibilidade e aprovação. Esta proposta é um documento comercial e não substitui contrato.
        </Text>

        <Text style={styles.footer}>Documento gerado em {generatedAtLabel} — CarManager</Text>
      </Page>
    </Document>
  );
};

export default ProposalPDF;

